import { Avatar, Box, Button, ButtonGroup, Grid, IconButton, Modal, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MainBorderRadius, TableBgcolor, TableHeader } from '../../../app/AppStyles';
import { SearchKeyFilter } from '../../../app/AppUtils';
import BackButton from '../../subcomponents/BackButton';
import ContentLoader from '../../subcomponents/ContentLoader';
import ListItemCard from '../../subcomponents/ListItemCard';
import PageLayout from '../../subcomponents/PageLayout';
import SearchField from '../../subcomponents/SearchField';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import SchoolclassGroupEditCard from './SchoolclassGroupEditCard';
import SchoolclassGroupLinkCard from './SchoolclassGroupLinkCard';
import { fetchSchoolclassGroup, resetSchoolclassGroupStatus } from './schoolclassGroupSlice';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/Remove';

export default function SchoolclassGroupPage() {
    const dispatch = useDispatch();
    const { id } = useParams();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { schoolclassGroup } = useSelector((state) => state.management.schoolclassGroup);

    const [filter, setFilter] = useState("");
    const [openEdit, setOpenEdit] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        dispatch(fetchSchoolclassGroup(id));
    }, [dispatch, id]);

    const handleFilterResult = (item) => {
        return SearchKeyFilter(item, ["schoolclassName", "schoolclassDescription"], filter);
    }

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - schoolclassGroup.data?.schoolclasses?.filter(handleFilterResult).length) : 0;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const callbackEditModal = () => {
        setOpenEdit(false);
        dispatch(resetSchoolclassGroupStatus());
    };

    const callbackAddModal = () => {
        setOpenAdd(false);
        dispatch(resetSchoolclassGroupStatus());
    };

    const callbackDeleteModal = () => {
        setOpenDelete(false);
        dispatch(resetSchoolclassGroupStatus());
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAddClick = () => {
        setOpenAdd(true);
    };

    const handleDeleteClick = () => {
        setOpenDelete(true);
    };

    return (
        <PageLayout
            title={`${schoolclassGroup.data?.schoolclassGroupName ?? "-"}`}
            content={
                <ContentLoader
                    successCondition={schoolclassGroup.status === "success"}
                    errorCondition={schoolclassGroup.status === "failed"}
                    content={
                        <>
                            <Grid container spacing={1} sx={{ mt: { xs: 0, md: 1 } }}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <ListItemCard backgroundStyle={{ height: 1 }}
                                        primaryText={schoolclassGroup.data?.schoolclassGroupName}
                                        secondaryText={resourceFinal[appLanguage].words.NAME}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <ListItemCard backgroundStyle={{ height: 1 }}
                                        primaryText={schoolclassGroup.data?.linkedSchoolclasses}
                                        secondaryText={resourceFinal[appLanguage].words.CLASSES}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <ListItemCard backgroundStyle={{ height: 1 }}
                                        primaryText={schoolclassGroup.data?.schoolclassGroupDescription ? schoolclassGroup.data?.schoolclassGroupDescription : "-"}
                                        secondaryText={resourceFinal[appLanguage].words.DESCRIPTION}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <ListItemCard backgroundStyle={{ height: 1 }}
                                        primaryText={schoolclassGroup.data?.linkedTimetableName ? schoolclassGroup.data?.linkedTimetableName : "-"}
                                        secondaryText={resourceFinal[appLanguage].words.TIMETABLE}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Stack sx={{ display: 'flex', flex: '1 1 auto', bgcolor: TableBgcolor, borderRadius: MainBorderRadius, p: 0.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
                                            <SearchField initialValue={filter} onDebouncedChange={setFilter} />
                                            <ButtonGroup>
                                                <Button variant="outlined" color="error" onClick={handleDeleteClick} startIcon={<RemoveIcon />}>{resourceFinal[appLanguage].words.DELETE}</Button>
                                                <Button variant="outlined" color="success" onClick={handleAddClick} startIcon={<AddIcon />}>{resourceFinal[appLanguage].words.ADD}</Button>
                                            </ButtonGroup>
                                        </Box>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.NAME}</TableCell>
                                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DESCRIPTION}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(rowsPerPage > 0 ? schoolclassGroup.data?.schoolclasses?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : schoolclassGroup.data?.schoolclasses?.filter(handleFilterResult))?.map(item =>
                                                        <TableRow key={item.schoolclassId} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                                                            <TableCell align="left">{item.schoolclassName}</TableCell>
                                                            <Tooltip title={item.schoolclassDescription}>
                                                                <TableCell align="left" sx={{
                                                                    whiteSpace: "nowrap",
                                                                    width: "50%",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis"
                                                                }}>{item.schoolclassDescription ? item.schoolclassDescription : "-"}</TableCell>
                                                            </Tooltip>
                                                        </TableRow>
                                                    )}
                                                    {(rowsPerPage > 0 ? schoolclassGroup.data?.schoolclasses?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : schoolclassGroup.data?.schoolclasses?.filter(handleFilterResult))?.length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                                                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={13} /></TableRow>}
                                                </TableBody>
                                                <TableFooter>
                                                    <TableRow>
                                                        <TablePagination
                                                            sx={{ border: 0 }}
                                                            rowsPerPageOptions={[5, 10, 25,
                                                                {
                                                                    label: schoolclassGroup.data?.schoolclasses.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                                                    value: schoolclassGroup.data?.schoolclasses.length < 100 ? -1 : 100
                                                                }
                                                            ]}
                                                            count={schoolclassGroup.data?.schoolclasses.filter(handleFilterResult).length ?? 0}
                                                            rowsPerPage={rowsPerPage}
                                                            page={page}
                                                            onPageChange={handleChangePage}
                                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                                            ActionsComponent={TablePaginationActions}
                                                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                                                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                                                        />
                                                    </TableRow>
                                                </TableFooter>
                                            </Table>
                                        </TableContainer>
                                    </Stack >
                                </Grid>
                            </Grid>
                            <Modal open={openEdit} onClose={callbackEditModal}>
                                <>
                                    <SchoolclassGroupEditCard handleClose={callbackEditModal} schoolclassGroupId={id} />
                                </>
                            </Modal>
                            <Modal open={openAdd} onClose={callbackAddModal}>
                                <>
                                    <SchoolclassGroupLinkCard handleClose={callbackAddModal} schoolclassGroupId={id} addMode />
                                </>
                            </Modal>
                            <Modal open={openDelete} onClose={callbackDeleteModal}>
                                <>
                                    <SchoolclassGroupLinkCard handleClose={callbackDeleteModal} schoolclassGroupId={id} deleteMode currentSchoolclasses={schoolclassGroup.data?.schoolclasses} />
                                </>
                            </Modal>
                        </>
                    }
                />
            } contentRight={
                <>
                    {schoolclassGroup.status === "success" &&
                        <Tooltip title={resourceFinal[appLanguage].words.EDIT} placement="left" arrow>
                            <IconButton onClick={() => setOpenEdit(true)}>
                                <Avatar sx={{ bgcolor: 'secondary.main', p: 0.5 }}>
                                    <EditIcon sx={{ color: 'white.main', fontSize: 40 }} />
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                    }
                    <BackButton desktop enableBorder />
                </>
            }
            bottomBar
            disableContentBackground
        />
    );
}

